<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, useNavigate } from 'svelte-navigator';
  import {
    Badge,
    Button,
    Column,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    Table,
    Tooltip
  } from '@sveltestrap/sveltestrap';

  import { Chart } from '@client/components/charts';
  import { EmptyState } from '@client/components/empty-state';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';
  import { Spinner as Loading } from '@client/components/spinner';

  import { Preview, Tab } from '@client/pages/bizcards/components';

  import { staticHexColors } from '@common/utils/colors';

  import PushModal from './pusher/push-modal.svelte';
  import {
    assignedUsers,
    assignments,
    deleteAssignment,
    disableAssignment,
    getPreview,
    inactiveRoutes,
    loadAssignments,
    loadInactiveRoutes,
    loading,
    loadingInactive,
    totalUsers,
    unassigned
  } from './stores/assign';
  import WelcomeModal from './welcome/welcome-modal.svelte';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();
  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const USER_TOOLTIP_LIMIT = 20;

  let chartOptions = {
    plugins: {
      title: { display: false },
      legend: { display: false }
    }
  };
  let pushed = false;
  let show = false;
  let showWelcome = false;
  let initialBizcard = null;

  $: if (!show && pushed) {
    pushed = false;
    loadAssignments().then(loadInactiveRoutes);
  }

  $: hasUnassigned = $unassigned > 0;
  $: chartData = {
    labels: [
      ...$assignments.map((a) => a.name),
      ...(hasUnassigned ? [$format('label.BIZCARD.ASSIGN.NO_BUSINESS_CARD')] : [])
    ],
    datasets: [
      {
        label: 'Users',
        data: [...$assignments.map((a) => a.userCount), ...(hasUnassigned ? [$unassigned] : [])],
        backgroundColor: [
          ...$assignments.map((_, i) => staticHexColors[i % staticHexColors.length]),
          ...(hasUnassigned ? ['#d3d3d3'] : [])
        ],
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };

  const onAssign = () => {
    show = true;
  };

  const onDelete = async (bizcard) => {
    const success = await deleteAssignment(bizcard);
    notify(
      $format(success ? 'label.SUCCESS.DELETE_ASSIGNMENT' : 'label.FAILURE.DELETE_ASSIGNMENT'),
      success ? 'success' : 'error'
    );
  };

  const onDisable = async (bizcard) => {
    const success = await disableAssignment(bizcard);
    notify(
      $format(success ? 'label.SUCCESS.DISABLE_ASSIGNMENT' : 'label.FAILURE.DISABLE_ASSIGNMENT'),
      success ? 'success' : 'error'
    );
  };

  const onWelcome = (name = null) => {
    initialBizcard = name;
    showWelcome = true;
  };

  onMount(async () => {
    await loadAssignments();
    loadInactiveRoutes();
  });
</script>

<div class="page">
  <PageHeader
    title={$format('label.ASSIGNMENT')}
    paths={[[$format('label.BUSINESS_CARDS'), '/bizcards/studio'], [$format('label.ASSIGNMENT')]]}
  >
    {#if $assignments.length}
      <Button on:click={() => onWelcome()} disabled={$loading || show || showWelcome} class="me-1">
        <Icon name="mail" class="d-md-none" />
        <span class="d-none d-md-inline">{$format('label.BIZCARD.ASSIGN.SEND_WELCOME_EMAIL')}</span>
      </Button>
    {/if}
    <Button color="primary" on:click={onAssign} disabled={$loading || show || showWelcome} data-testid="assign-bizcard">
      <Icon name="plus" class="d-md-none" />
      <span class="d-none d-md-inline">{$format('label.BIZCARD.ASSIGN.ASSIGN_BUSINESS_CARD')}</span>
    </Button>
    <PushModal bind:show bind:pushed />

    <WelcomeModal assignments={$assignments} {initialBizcard} bind:show={showWelcome} />
  </PageHeader>

  <div class="page-content">
    <Nav pills>
      <Tab active on:click={() => navigate('/bizcards/assignment')}>{$format('label.ASSIGNMENT')}</Tab>
      <Tab on:click={() => navigate('/bizcards/assignment/routing')}>{$format('label.ROUTING')}</Tab>
      <Tab on:click={() => navigate('/bizcards/assignment/history')}>{$format('label.HISTORY')}</Tab>
    </Nav>

    {#if $loading}
      <Loader backdrop show />
    {:else if $assignments.length === 0}
      <EmptyState
        icon="users"
        heading={$format('label.EMPTY_STATE.BUSINESS_CARD_ASSIGNMENT_HEADING')}
        description={$format('label.EMPTY_STATE.BUSINESS_CARD_ROUTING_DESCRIPTION')}
        action={$format('label.BIZCARD.ASSIGN.ASSIGN_BUSINESS_CARD')}
        on:click={onAssign}
      />
    {:else}
      <div class="assignments-wrapper py-3">
        <div class="chart-container px-3">
          <div class="chart-box">
            <h3 class="py-3">
              {$format('label.BIZCARD_USERS')}
            </h3>
            <Chart type="doughnut" bind:data={chartData} bind:options={chartOptions} height="250px" width="250px" />
            <ul class="chart-legend">
              {#each chartData.labels as label, i}
                <li>
                  <span class="dot" style="background-color: {chartData.datasets[0].backgroundColor[i]}" />
                  {label}
                </li>
              {/each}
            </ul>
          </div>
        </div>

        <div class="table-container">
          <Table rows={$assignments} let:row style="vertical-align: middle">
            <Column
              header={$format('label.BIZCARD.ASSIGN.BUSINESS_CARD')}
              footer={$format('label.BIZCARD.ASSIGN.TOTAL_USERS')}
              width="50%"
            >
              <div class="bizcard-cell">
                {#await getPreview(row.name)}
                  <Preview width={50} height={90} loading />
                {:then html}
                  <Preview width={50} height={90} scale={0.14} title={row.name} {html} />
                {/await}
                <Badge color="light" class="text-small border bg-white">{row.name}</Badge>
              </div>
            </Column>
            <Column header={$format('label.BIZCARD.ASSIGN.GROUPS')} width="40%">
              {#each row.groups as group}
                <Link to="/manage/groups/{encodeURIComponent(group)}">
                  <Badge color="light" class="text-small border bg-white me-1 mb-1">
                    {group}
                  </Badge>
                </Link>
              {/each}
            </Column>
            <Column class="text-end" width="10%">
              <div slot="header" class="text-end">
                {$format('label.USERS')}
              </div>
              <span id="tooltip-user-count-{row.name}">{row.userCount}</span>
              {#if row.users?.length}
                <Tooltip target="tooltip-user-count-{row.name}">
                  {#each row.users.slice().sort().slice(0, USER_TOOLTIP_LIMIT) as user}
                    <div>{user}</div>
                  {/each}
                  {#if row.users.length > USER_TOOLTIP_LIMIT}
                    <div>+ {row.users.length - USER_TOOLTIP_LIMIT} more</div>
                  {/if}
                </Tooltip>
              {/if}
              <div slot="footer" class="text-end">{$assignedUsers}</div>
            </Column>
            <Column class="text-end" width="3rem">
              <div slot="header" class="text-end">%</div>
              {$totalUsers ? ((row.userCount / $totalUsers) * 100).toFixed(1) + '%' : '-'}
              <div slot="footer" class="text-end">
                {$totalUsers ? (($assignedUsers / $totalUsers) * 100).toFixed(1) : 0}%
              </div>
            </Column>
            <Column class="text-end" width="3rem">
              <Dropdown>
                <DropdownToggle class="border-0 px-1">
                  <Icon name="more-vertical" />
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem on:click={() => onWelcome(row.name)}>
                    {$format('label.BIZCARD.ASSIGN.SEND_WELCOME_EMAIL')}
                  </DropdownItem>
                  <DropdownItem on:click={() => onDisable(row.name)}>
                    {$format('label.BIZCARD.ASSIGN.DISABLE')}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem class="text-danger" on:click={() => onDelete(row.name)}>
                    {$format('label.BIZCARD.ASSIGN.DELETE')}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </Column>
          </Table>

          {#if $loadingInactive}
            <Loading show />
          {:else if $inactiveRoutes.length}
            <div class="py-3">
              <h3>{$format('label.BIZCARD.ASSIGN.INACTIVE_TITLE')}</h3>
              <p>{$format('label.BIZCARD.ASSIGN.INACTIVE_DESCRIPTION')}</p>

              <Table rows={$inactiveRoutes} let:row style="vertical-align: middle" class="mt-4">
                <Column header={$format('label.BIZCARD.ASSIGN.BUSINESS_CARD')} width="50%">
                  <div class="bizcard-cell" style:filter="grayscale()">
                    {#await getPreview(row.name)}
                      <Preview width={50} height={90} loading />
                    {:then html}
                      <Preview width={50} height={90} scale={0.14} title={row.name} {html} />
                    {/await}
                    <Badge color="light" class="text-small border bg-white">{row.name}</Badge>
                  </div>
                </Column>
                <Column header={$format('label.BIZCARD.ASSIGN.GROUPS')} width="40%">
                  {#each row.groups as group}
                    <Badge color="light" class="text-small border bg-white me-1 mb-1">{group}</Badge>
                  {/each}
                </Column>
                <Column class="text-end" width="10%">
                  <div slot="header" class="text-end">{$format('label.USERS')}</div>
                  -
                </Column>
                <Column class="text-end" width="3rem">
                  <div slot="header" class="text-end">%</div>
                  -
                </Column>
                <Column class="text-end" width="3rem">
                  <Dropdown>
                    <DropdownToggle class="border-0" disabled>
                      <Icon name="more-vertical" />
                    </DropdownToggle>
                  </Dropdown>
                </Column>
              </Table>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .bizcard-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .chart-legend {
    list-style: none;
    padding: 0;
    margin: 1rem auto 0;
    width: fit-content;

    li {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 4px;
      font-size: 0.85rem;
    }

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }
  }

  .assignments-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    width: 100%;
  }

  .chart-container {
    flex: 0 0 250px;
    margin: 0 auto;
  }

  .table-container {
    flex: 1 1 725px;
    min-width: 0;
  }

  @media (max-width: 725px) {
    .table-container {
      overflow-x: auto;
    }
  }
</style>
